import '@atlaskit/css-reset/bundle.css';

import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { FlagsProvider } from '@atlaskit/flag';
import './index.css';
import { HelmetProvider } from 'react-helmet-async';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { ErrorBoundary } from './components/ErrorBoundary';
import { initializeSentry } from './bootstrap/sentry';
import RequireAuth from './components/RequireAuth';
import { persister, queryClient } from './ExtensionApp';
import { shouldDehydratePersistedQuery } from './queryPersistence';
import { AppIntlProvider } from './i18n/AppIntlProvider';
import { initUFO, UFOSuspenseFallback } from './analytics/react-ufo';
import { ExtensionInitTimingUFO } from './analytics/react-ufo/ExtensionInitTimingUFO';
import { ThemeProvider } from './hooks/useThemeContext';
import { PerformanceSLO, PerformanceSLOKeys } from './observability';
import { setLocalDevSessionCookie } from './config';

if (process.env.IS_LOCAL_DEV_ATLASPACK_BUILD === 'true') {
	void setLocalDevSessionCookie();
}

const HomeLoginPageLazy = React.lazy(
	() => import(/* webpackChunkName: "home-login" */ './components/home/HomeLoginPage'),
);

initializeSentry();

// Initialize UFO as early as possible to capture more initialization time
initUFO();

const container = document.getElementById('root');
const root = createRoot(container!);

const LazyApp = React.lazy(() => import('./App'));

// Component to handle extension timing data within UFO context
const ExtensionTimingHandler = (): React.JSX.Element => {
	return <ExtensionInitTimingUFO />;
};

root.render(
	<PerformanceSLO ufo={PerformanceSLOKeys.APPLICATION_BOOTSTRAP}>
		<ExtensionTimingHandler />
		<HelmetProvider>
			<AppIntlProvider>
				<ErrorBoundary>
					<BrowserRouter>
						<FlagsProvider>
							<PersistQueryClientProvider
								client={queryClient}
								persistOptions={{
									persister,
									dehydrateOptions: {
										shouldDehydrateQuery: shouldDehydratePersistedQuery,
									},
								}}
							>
								<ThemeProvider>
									<Suspense fallback={<UFOSuspenseFallback name="main-app-loading" />}>
										<RequireAuth
											Component={LazyApp}
											LoginComponent={HomeLoginPageLazy}
											publicRoutes={[
												'/extension/welcome',
												'/extension/log-out',
												'/extension/ntp-logout-redirect',
											]}
										/>
									</Suspense>
								</ThemeProvider>
							</PersistQueryClientProvider>
						</FlagsProvider>
					</BrowserRouter>
				</ErrorBoundary>
			</AppIntlProvider>
		</HelmetProvider>
	</PerformanceSLO>,
);
